import { useEffect, useRef, useState } from 'react';
import useDevicesStore from 'store/devices';
import { getAllChildDevicesByType } from 'util/device';
import api from 'util/api';
import {
  bootstrapChartData,
  defaultOptions,
  getTimeScaleOptions,
  Bar
} from 'components/charts';
import {
  Banner,
  DateRangePicker,
  LoadingIndicator
} from '@thermon-platforms/thermon-ui-core';
import { cloneDeep, merge } from 'lodash';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';


function Network() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showControllers, setShowControllers] = useState(false);
  const [devicesStore] = useDevicesStore();
  const [networkHealth, setNetworkHealth] = useState(bootstrapChartData());
  const [allNetworkHealth, setAllNetworkHealth] = useState(bootstrapChartData());
  const [searchQuery, setSearchQuery] = useState('');
  const site = devicesStore.devices.find(d => d.type == 'Site');
  const bridges = getAllChildDevicesByType(site, 'Bridge').filter(bridge => bridge.deviceModel != 49); // Filter out virtual bridges
  const now = new Date();
  const [range, setRange] = useState({
    start: new Date(now - (7 * 24 * 60 * 60 * 1000)).toISOString(),
    end: new Date(now).toISOString()
  });
  // Keep live as a ref to avoid race conditions between interval updates and UI actions.
  const live = useRef(true);
  const interval = useRef();

  function parseAvailability(value) {
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'number') return value > 0 ? 1 : 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' ? 1 : 0;
    }
    return 0;
  }

  function getAvailabilityLabel(value) {
    return value == 1 ? t`Available` : t`Unavailable`;
  }

  function normalizeTimestamp(value) {
    if (value instanceof Date) return value.getTime();

    if (typeof value === 'number') {
      return value < 1e12 ? value * 1000 : value;
    }

    if (typeof value === 'string') {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric < 1e12 ? numeric * 1000 : numeric;
      }

      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? NaN : parsed;
    }

    return NaN;
  }

  function setRangeToNow() {
    setRange((currentRange) => {
      const nextNow = new Date();
      return {
        start: currentRange.start || new Date(nextNow - (7 * 24 * 60 * 60 * 1000)).toISOString(),
        end: nextNow.toISOString()
      };
    });
  }

  function setLive() {
    if (interval.current) clearInterval(interval.current);
    setRangeToNow();
    interval.current = setInterval(setRangeToNow, 1 * 60 * 1000);
    live.current = true;
  }

  function pauseLive() {
    if (interval.current) clearInterval(interval.current);
    live.current = false;
  }

  useEffect(() => {
    interval.current = setInterval(setRangeToNow, 1 * 60 * 1000);
    return () => {
      if (interval.current) clearInterval(interval.current);
    };
  }, []);

  useEffect(() => {
    const currentNow = new Date();
    if (currentNow - new Date(range.end) < (1 * 60 * 1000)) {
      live.current = true;
    } else {
      live.current = false;
    }
  }, [range]);

  useEffect(() => {
    if (!bridges?.length) return;
    setLoading(true);
    api('/devices/availabilityHistory', {
      method: 'POST',
      body: JSON.stringify({
        deviceIds: (() => {
          if (showControllers) {
            const ids = [];
            bridges.forEach(bridge => {
              ids.push(bridge.id);
              const controllers = getAllChildDevicesByType(bridge, 'Controller');
              controllers.forEach(controller => {
                if ([28, 31].includes(controller.deviceModel)) return; // Filter out Duo and virtual controllers
                ids.push(controller.id);
              });
            });
            return ids;
          }
          return bridges.map(d => d.id);
        })(),
        startDate: range.start,
        endDate: range.end
      })
    })
      .then(res => res.json())
      .then(data => {
        setError(null);
        const chartData = bootstrapChartData();

        const devices = data?.devices || [];
        const rangeEndMs = normalizeTimestamp(range.end);

        chartData.labels = devices.map(d => d.deviceId);

        const segments = devices.flatMap((device) => {
          const timestamps = device.timestamps || [];
          const availability = device.availability || [];

          return timestamps
            .map((start, index) => {
              const startMs = normalizeTimestamp(start);
              const nextTimestamp = timestamps[index + 1] || rangeEndMs;
              const endMs = normalizeTimestamp(nextTimestamp);

              return {
                x: [startMs, endMs],
                y: device.deviceId,
                deviceName: device.deviceName,
                availability: parseAvailability(availability[index])
              };
            })
            .filter(segment => (
              Number.isFinite(segment.x[0]) &&
              Number.isFinite(segment.x[1]) &&
              segment.x[0] < segment.x[1]
            ));
        });

        chartData.datasets = [{
          label: t`Availability`,
          data: segments,
          grouped: false,
          borderSkipped: false,
          barPercentage: 1,
          categoryPercentage: 0.9,
          backgroundColor: (context) => (
            context.raw?.availability == 1 ?
              'rgba(39,84,121,1)' :
              'rgba(218,41,28,1)'
          ),
          parsing: {
            xAxisKey: 'x',
            yAxisKey: 'y'
          }
        }];

        setAllNetworkHealth(chartData);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [devicesStore.devices, range, showControllers]);

  useEffect(() => {
    const query = (searchQuery || '').trim().toLowerCase();

    if (!query || query.length < 2) {
      setNetworkHealth(allNetworkHealth);
      return;
    }

    const filteredLabels = (allNetworkHealth.labels || []).filter((label) => {
      const device = devicesStore.shallowDevices.get(label);
      return String(device?.model.name).toLowerCase().includes(query);
    });

    setNetworkHealth({
      ...allNetworkHealth,
      labels: filteredLabels,
      datasets: (allNetworkHealth.datasets || []).map((dataset) => ({
        ...dataset,
        data: (dataset.data || []).filter((segment) =>
          String(segment?.deviceName || segment?.y || '').toLowerCase().includes(query)
        )
      }))
    });
  }, [searchQuery, allNetworkHealth]);

  return (<>
    <Banner status="alert" show={error}>
      <Trans>There was an issue loading network health data.</Trans>
    </Banner>
    <div className="chart-container">
      <div className="controls">
        <DateRangePicker
          start={range.start}
          end={range.end}
          onChange={setRange}
        />
        <span className="search-input">
          <input
            type="text"
            className="search"
            placeholder={t`Search Devices`}
            value={searchQuery}
            onChange={event => {
              setSearchQuery(event.target.value);
            }}
          />
        </span>
        <label>
          <input
            type="checkbox"
            onChange={event => setShowControllers(event.target.checked)}
          />&nbsp;
          <Trans>Show Controllers</Trans>
        </label>
        <LoadingIndicator show={loading} />
        <span
          className={`live-indicator ${live.current ? 'active' : ''}`}
          onClick={setLive}
        >
          <Trans>Live</Trans>
        </span>
      </div>
      <div className="chart" style={{ height: Math.max(((networkHealth?.labels?.length ?? 0) * 24), 600) }}>
        <Bar
          data={networkHealth}
          options={
            merge(cloneDeep(defaultOptions.bar), {
              indexAxis: 'y',
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    title: () => null,
                    label: (context) => {
                      const deviceName = context.raw?.deviceName;
                      const availability = getAvailabilityLabel(context.raw?.availability);
                      const [start, end] = context.raw?.x || [];
                      const rangeLabel = (start && end) ?
                        `${new Date(start).toLocaleString()} - ${new Date(end).toLocaleString()}` :
                        '';
                      return `${deviceName}: ${availability}${rangeLabel ? ` (${rangeLabel})` : ''}`;
                    }
                  }
                },
                zoom: {
                  pan: {
                    enabled: true,
                    mode: 'xy',
                    onPanComplete: pauseLive
                  },
                  zoom: {
                    wheel: { enabled: true },
                    pinch: { enabled: true },
                    mode: 'xy',
                    onZoomComplete: pauseLive
                  }
                }
              },
              scales: {
                x: {
                  ...getTimeScaleOptions(),
                  min: normalizeTimestamp(range.start),
                  max: normalizeTimestamp(range.end),
                  title: {
                    display: true,
                    text: t`Time`
                  }
                },
                y: {
                  type: 'category',
                  ticks: {
                    autoSkip: false,
                    callback: function(value) {
                      const deviceId = this.chart.data.labels[value];
                      return devicesStore.shallowDevices.get(deviceId)?.model.name || deviceId;
                    }
                  },
                  title: {
                    display: true,
                    text: t`Device`
                  }
                }
              }
            })
          }
        />
      </div>
    </div>
  </>);
}

export default Network;
